<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import {
    ChevronLeft,
    ChevronRight,
    CalendarDays,
    LayoutGrid,
    MessageSquare,
    AlertTriangle,
    FileText,
    Rocket,
    Clock
  } from 'lucide-svelte';
  import { adminActivityApi } from '$lib/api/admin';
  import { getErrorMessage } from '$lib/api/client';
  import {
    activityDomainLabel,
    activityKind,
    dayLabel,
    istDay,
    istTime,
    shiftDay
  } from '$lib/utils/format';
  import { route } from '$lib/state/route.svelte';
  import Avatar from '$lib/components/ui/Avatar.svelte';
  import Chip from '$lib/components/ui/Chip.svelte';
  import EmptyState from '$lib/components/ui/EmptyState.svelte';
  import type { DailyActivityGroup } from '$lib/types';

  const { date }: { date: string } = $props();

  const today = istDay();
  const isToday = $derived(date === today);

  const dailyQuery = createQuery(() => ({
    queryKey: ['admin-daily-activity', date],
    queryFn: () => adminActivityApi.daily(date)
  }));

  // Day changes are user-initiated navigation, so each one is its own
  // history entry — Back walks back through the days you flipped through.
  function go(day: string) {
    route.push({ tab: 'daily', date: day });
  }

  const groups = $derived(dailyQuery.data?.groups ?? []);
  const total = $derived(dailyQuery.data?.count ?? 0);

  // Summary strip under the head: actions, people, projects touched, deploys.
  const stats = $derived.by(() => {
    const projects = new Set<string>();
    let deploys = 0;
    for (const g of groups) {
      for (const a of g.activities) {
        projects.add(a.project.name);
        if (a.type === 'deploy') deploys++;
      }
    }
    return { actions: total, people: groups.length, projects: projects.size, deploys };
  });

  // Per-type tally for a person's chip row, busiest type first.
  function typeCounts(g: DailyActivityGroup): Array<[string, number]> {
    const counts = new Map<string, number>();
    for (const a of g.activities) counts.set(a.type, (counts.get(a.type) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }

  // Same domain-prefix rule the chip colors use (see activityKind) — a new
  // verb inherits its domain's icon instead of falling back to the clock.
  const ICONS: Record<string, any> = {
    task: LayoutGrid,
    ticket: MessageSquare,
    debt: AlertTriangle,
    doc: FileText,
    deploy: Rocket
  };
  function actIcon(type: string) {
    return ICONS[type.split('_')[0]] ?? Clock;
  }
</script>

<div class="page-head day-head">
  <div>
    <h1 class="h-title">Daily activity</h1>
    <div class="h-sub">
      Who did what across every project
      {#if dailyQuery.data}
        · {total}
        {total === 1 ? 'action' : 'actions'} by {groups.length}
        {groups.length === 1 ? 'person' : 'people'}
      {/if}
    </div>
  </div>
  <span class="spacer"></span>
  <div class="day-nav">
    <button
      type="button"
      class="icon-btn day-arrow"
      onclick={() => go(shiftDay(date, -1))}
      aria-label="Previous day"
      title="Previous day"
    >
      <ChevronLeft size={18} />
    </button>
    <div class="day-label">
      <div class="dl-main">{isToday ? 'Today' : dayLabel(date)}</div>
      <div class="dl-sub">{date} · IST</div>
    </div>
    <button
      type="button"
      class="icon-btn day-arrow"
      onclick={() => go(shiftDay(date, 1))}
      disabled={isToday}
      aria-label="Next day"
      title={isToday ? 'Today is the latest day' : 'Next day'}
    >
      <ChevronRight size={18} />
    </button>
  </div>
  <button type="button" class="btn btn-secondary today-btn" onclick={() => go(today)} disabled={isToday}>
    Today
  </button>
</div>

{#if dailyQuery.isLoading}
  <p style="color:var(--t3);font-size:13.5px">Loading activity…</p>
{:else if dailyQuery.isError}
  <p style="color:var(--danger);font-size:13.5px">{getErrorMessage(dailyQuery.error)}</p>
{:else if groups.length}
  {#if dailyQuery.data?.truncated}
    <p style="color:var(--warn);font-size:12.5px;margin:0 0 14px">
      Showing the {groups.reduce((n, g) => n + g.activities.length, 0)} most recent of {total} actions
      on this day.
    </p>
  {/if}

  <div class="day-summary">
    <div class="stat">
      <div class="stat-n">{stats.actions}</div>
      <div class="stat-l">Actions</div>
    </div>
    <div class="stat">
      <div class="stat-n">{stats.people}</div>
      <div class="stat-l">People</div>
    </div>
    <div class="stat">
      <div class="stat-n">{stats.projects}</div>
      <div class="stat-l">Projects</div>
    </div>
    <div class="stat">
      <div class="stat-n">{stats.deploys}</div>
      <div class="stat-l">Deploys</div>
    </div>
  </div>

  <div class="day-groups">
    {#each groups as g (g.user_id ?? 'system')}
      <div class="card">
        <div class="dg-head">
          <Avatar id={g.user_id ?? 0} name={g.name} size={34} />
          <div style="min-width:0">
            <div class="dg-name">{g.name}</div>
            <div class="dg-sub">
              {g.email || 'Automated · intake and deploy scripts'} · {g.count}
              {g.count === 1 ? 'action' : 'actions'}
            </div>
          </div>
          <span class="spacer"></span>
          <div class="dg-chips">
            {#each typeCounts(g) as [type, n] (type)}
              <Chip label="{activityDomainLabel(type)} {n}" kind={activityKind(type)} />
            {/each}
          </div>
        </div>
        {#each g.activities as a (a.id)}
          {@const Icon = actIcon(a.type)}
          <div class="divider"></div>
          <div class="act-row">
            <span class="act-time">{istTime(a.created_at)}</span>
            <!-- The raw type is a tooltip on the icon, not a visible string:
                 the text below now says what happened in words. -->
            <div class="act-ic" title={a.type}>
              <Icon size={16} />
            </div>
            <!-- The server's own sentence, verbatim and unclipped ("Moved
                 WHP-101 to Done: Wire up guest-room scene presets") — it is
                 the only part of the row that says what actually happened,
                 so it wraps rather than truncates. -->
            <div style="flex:1;min-width:0">
              <div class="act-tx">{a.text}</div>
              <div class="act-meta">
                <span class="act-when">{istTime(a.created_at)}</span>
                <span class="act-proj">{a.project.name}</span>
              </div>
            </div>
            <Chip label={a.project.name} kind="gray" />
          </div>
        {/each}
      </div>
    {/each}
  </div>
{:else}
  <EmptyState
    icon={CalendarDays}
    heading={isToday ? 'Nothing logged today yet' : `Nothing logged on ${dayLabel(date)}`}
    sub="Task moves, triage, deploys, debt and doc changes across every project show up here, grouped by who did them. Use ◀ / ▶ to check another day."
  />
{/if}
